import { CircularProgress, Grid } from "@material-ui/core";
import { Box, Button, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import PersonIcon from "@mui/icons-material/Person";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../../services/HttpService";
import CRMTextField from "../../components/crm/CRMTextField";
import UserService from "../../services/UserService";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DevicesIcon from "@mui/icons-material/Devices";
import Labels from "../../labels/Labels";

export default function CardHolder({ changeParentTo, idCustomer }) {
  const queryClient = useQueryClient();
  const [loadingFields, setLoadingFields] = useState([]);

  // #region Locations
  async function fetchLocations() {
    if (UserService.hasRole(["pc-query-locations"])) {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/locations`
      );
      return response;
    }
    return [];
  }
  const { data: dataLocations } = useQuery(["locations"], fetchLocations);
  const locations = (dataLocations?.data || []).map((loc) => ({
    value: String(loc.id),
    label: loc.description,
  }));
  // Save Location

  const cardHolderMutation = useMutation(
    (body) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/member/admin-card`,
        body
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["cardholder", idCustomer]);
      },
      onError: (err) => {
        console.error(err);
      },
    }
  );

  // console.log(loadingFields);

  const newLocationSave = (newLocation) => {
    if (!newLocation) {
      console.error("No new location provided");
      return;
    }
    if (newLocation === "0") {
      console.error("Invalid location selected");
      return;
    }
    if (newLocation === data?.location?.value) {
      console.log("No change in location, skipping update");
      return;
    }
    setLoadingFields((fields) => [...fields, "location"]);
    const body = {
      customerId: idCustomer,
      locationId: newLocation,
    };
    cardHolderMutation.mutateAsync(body);
  };

  const handleUnlockUser = () => {
    const body = {
      customerId: idCustomer,
      status: "UNLOCK-USER",
    };
    cardHolderMutation.mutate(body);
  };
  // #endregion

  // #region Main Query
  const { data, isLoading, isFetching, isError } = useQuery(
    ["cardholder", idCustomer],
    async () => {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/member/${idCustomer}`
      );
      return response.data;
    }
  );

  useEffect(() => {
    if (!isFetching) {
      setLoadingFields([]);
    }
  }, [isFetching]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box p={3}>
        <Typography color="error">Failed to load cardholder data.</Typography>
      </Box>
    );
  }
  const actionPending = Boolean(data?.actionPending?.value);
  const statusDisplay = actionPending
    ? "Change in Progress..."
    : data?.status?.value;
  // #endregion

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" height="100%">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          borderBottom={1}
          borderColor="divider"
        >
          <Typography variant="h6" style={{ fontWeight: "bold" }}>
            {data?.name?.value}
          </Typography>
        </Box>
        <Box flex={1} p={2}>
          <Box display="flex" alignItems="flex-start">
            <PersonIcon sx={{ fontSize: 72, mr: 3, color: "grey.600" }} />
            <Box flex={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                  <Box display="flex" alignItems="center" sx={{ gap: 1.5, flexWrap: "wrap" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        px: 1.5,
                        py: 1,
                        bgcolor: "grey.50",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 36,
                          height: 36,
                          flexShrink: 0,
                          borderRadius: 1.5,
                          bgcolor: "rgba(13, 127, 129, 0.1)",
                          color: "#0d7f81",
                        }}
                      >
                        <DevicesIcon fontSize="small" />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", fontWeight: 500, lineHeight: 1.4 }}
                        >
                          {Labels.getLabel("manageCard.enrollmentStatus", "App/website enrollment status")}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, lineHeight: 1.5, overflowWrap: "anywhere" }}
                        >
                          {statusDisplay}
                        </Typography>
                      </Box>
                    </Box>
                    {!actionPending &&
                      data?.status?.value === "LOCKED" &&
                      UserService.hasRole(["pc-lock-unlock-cards"]) && (
                        <Button
                          variant="outlined"
                          size="small"
                          style={{ marginRight: 4 }}
                          color="primary"
                          startIcon={<LockOpenIcon />}
                          disabled={
                            isFetching ||
                            cardHolderMutation.isLoading ||
                            actionPending
                          }
                          onClick={handleUnlockUser}
                        >
                          {cardHolderMutation.isLoading
                            ? "UNLOCKING..."
                            : "UNLOCK"}
                        </Button>
                      )}
                  </Box>
                </Grid>
                {data.name && (
                  <Grid item xs={12} sm={6}>
                    <CRMTextField
                      entity="member"
                      entityId={idCustomer}
                      fieldName="name"
                      label="Full Name"
                      field={data.name}
                    />{" "}
                  </Grid>
                )}
                {data.email && (
                  <Grid item xs={12} sm={6}>
                    <CRMTextField
                      entity="member"
                      entityId={idCustomer}
                      fieldName="email"
                      label="Email"
                      field={data.email}
                    />{" "}
                  </Grid>
                )}
                {data.username && (
                  <Grid item xs={12} sm={6}>
                    <CRMTextField
                      entity="member"
                      entityId={idCustomer}
                      fieldName="username"
                      label="Username"
                      field={data.username}
                    />{" "}
                  </Grid>
                )}
                {data.phone && (
                  <Grid item xs={12} sm={6}>
                    <CRMTextField
                      entity="member"
                      entityId={idCustomer}
                      fieldName="phone"
                      label="Phone"
                      field={data.phone}
                    />{" "}
                  </Grid>
                )}
                {data.address && (
                  <Grid item xs={12} sm={6}>
                    <CRMTextField
                      entity="member"
                      entityId={idCustomer}
                      fieldName="address"
                      label="Address"
                      field={data.address}
                    />{" "}
                  </Grid>
                )}
                {data.employer && (
                  <Grid item xs={12} sm={6}>
                    <CRMTextField
                      entity="member"
                      entityId={idCustomer}
                      fieldName="employer"
                      label="Company"
                      field={data.employer}
                    />{" "}
                  </Grid>
                )}
                {data.employeeId && (
                  <Grid item xs={12} sm={6}>
                    <CRMTextField
                      entity="member"
                      entityId={idCustomer}
                      fieldName="employeeId"
                      label="Member code"
                      field={data.employeeId}
                    />{" "}
                  </Grid>
                )}
                {data.createdAt && (
                  <Grid item xs={12} sm={6}>
                    <CRMTextField
                      entity="member"
                      entityId={idCustomer}
                      fieldName="createdAt"
                      label="Created at"
                      field={data.createdAt}
                    />{" "}
                  </Grid>
                )}
                {data.location && (
                  <Grid item xs={12} sm={6}>
                    <CRMTextField
                      entity="member"
                      entityId={idCustomer}
                      fieldName="location"
                      label="Location"
                      field={data.location}
                      editorType="select"
                      selectOptions={locations}
                      loading={loadingFields.includes("location")}
                      {...(!UserService.hasRole(["pf-only-disburse"]) && {
                        onChange: (newLocation) => newLocationSave(newLocation),
                      })}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </Fragment>
  );
}
